import BeforeYouSleep from "@/components/BeforeYouSleep";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import TheField from "@/components/TheField";
import WelcomeOverlay from "@/components/WelcomeOverlay";
import TonightCard from "@/components/TonightCard";
import { Button } from "@/components/ui/button";
import { ArrowRight, Flame, Users, Moon, TrendingUp } from "lucide-react";

const greeting = () => { const h = new Date().getHours(); return h < 12 ? "Good morning" : h < 18 ? "Good afternoon" : "Good evening"; };

function Tile({ icon: Icon, label, sub, onClick, testid }) {
  return (
    <motion.button whileHover={{ y: -3 }} onClick={onClick} data-testid={testid}
      className="text-left rounded-3xl p-5 glass border border-white/10 hover:border-primary/40 transition-colors duration-300">
      <span className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/15 text-primary mb-4"><Icon className="w-5 h-5" /></span>
      <div className="font-serif text-lg leading-tight">{label}</div>
      <div className="text-xs text-muted-foreground mt-0.5">{sub}</div>
    </motion.button>
  );
}

export default function Home() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [home, setHome] = useState(null);

  useEffect(() => { api.get("/home").then((r) => setHome(r.data)).catch(() => {}); }, []);

  const level = home?.level?.name || "Awareness";
  const nextLevel = home?.level?.next;
  const xp = home?.social_xp || 0;
  const streak = home?.streak || 0;
  const firstName = user?.name?.split(" ")[0] || "friend";
  const toNext = nextLevel ? Math.max(0, nextLevel.at - xp) : 0;
  const barPct = nextLevel ? Math.min(100, Math.round((xp / nextLevel.at) * 100)) : 100;
  const tonightId = home?.tonight?.id;

  return (
    <div className="min-h-screen bg-background relative overflow-hidden">
      <WelcomeOverlay name={user?.name} userId={user?.id} />
      {/* ambient glows */}
      <div className="pointer-events-none absolute -top-40 left-1/2 -translate-x-1/2 w-[620px] h-[620px] rounded-full opacity-30"
        style={{ background: "radial-gradient(circle, hsl(195 100% 55% / 0.4) 0%, transparent 62%)", filter: "blur(70px)" }} />
      <div className="pointer-events-none absolute bottom-0 -right-28 w-[440px] h-[440px] rounded-full opacity-25"
        style={{ background: "radial-gradient(circle, hsl(12 90% 62%) 0%, transparent 60%)", filter: "blur(80px)" }} />
      <Navbar />

      <main className="relative max-w-md sm:max-w-lg mx-auto px-5 sm:px-8 pt-3 pb-36 md:pb-20 flex flex-col items-center text-center">
        {/* level + streak */}
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-[11px] uppercase tracking-[0.25em] text-primary mb-3 flex items-center gap-2">
          {level}{streak > 0 && <><span className="w-1 h-1 rounded-full bg-primary/50" /><Flame className="w-3 h-3" /> {streak} day{streak > 1 ? "s" : ""}</>}
        </motion.p>

        {/* presence orb */}
        <motion.div initial={{ opacity: 0, scale: 0.85 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1, ease: "easeOut" }}
          className="relative mb-7" data-testid="home-presence">
          <TheField intensity={0.9} size={168} active>
            <motion.div animate={{ y: [0, -6, 0] }} transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
              className="rounded-full overflow-hidden ring-1 ring-primary/25 shadow-2xl" style={{ width: 138, height: 138 }}>
              <img src="/adelaida/neutral.jpg" alt="Adelaida" className="w-full h-full object-cover" style={{ objectPosition: "center 22%" }} />
            </motion.div>
          </TheField>
          <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2 px-3 py-1.5 rounded-full glass border border-primary/20 whitespace-nowrap">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary/60" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-primary" />
            </span>
            <span className="text-[11px] text-foreground/80">Xcelio is here</span>
          </div>
        </motion.div>

        <motion.h1 initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.15 }}
          className="font-serif text-3xl sm:text-4xl tracking-tight mb-3">{greeting()}, {firstName}.</motion.h1>

        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}
          className="text-sm text-muted-foreground leading-relaxed max-w-sm mb-6 min-h-[2.5rem]" data-testid="home-briefing">
          {home?.briefing?.message || <span className="inline-block w-48 h-4 rounded bg-muted animate-pulse align-middle" />}
        </motion.p>

        {/* HERO — talk to Adelaide */}
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }} className="w-full mb-4">
          <Button size="lg" onClick={() => navigate("/talk?call=1")} data-testid="home-talk"
            className="w-full rounded-full py-6 text-base group bg-gradient-to-r from-blue-500 to-teal-400 hover:opacity-95 shadow-lg shadow-blue-500/25">
            Talk to Xcelio <ArrowRight className="w-4 h-4 ml-1.5 transition-transform duration-300 group-hover:translate-x-1" />
          </Button>
        </motion.div>

        <div className="w-full mt-8"><BeforeYouSleep /></div>
        <TonightCard />

        {/* slim progress */}
        <motion.button initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }}
          onClick={() => navigate("/progress")} data-testid="home-progress" className="w-full mb-6 text-left">
          <div className="flex items-center justify-between text-xs text-muted-foreground mb-2">
            <span>{level}</span>
            <span>{nextLevel ? `${toNext} XP to ${nextLevel.name}` : "Top level"}</span>
          </div>
          <div className="h-3 rounded-full bg-muted overflow-hidden shadow-inner">
            <motion.div initial={{ width: 0 }} animate={{ width: `${barPct}%` }} transition={{ duration: 1.1, delay: 0.6, ease: "easeOut" }}
              className="h-full rounded-full bg-gradient-to-r from-teal-400 to-blue-500" style={{ boxShadow: "0 0 10px rgba(79,182,196,0.55)" }} />
          </div>
        </motion.button>

        {/* three clean tiles */}
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.55 }}
          className="grid grid-cols-2 gap-3 w-full" data-testid="home-tiles">
          <Tile icon={Users} label="Practice" sub="Role-play a moment" onClick={() => navigate("/practice")} testid="home-tile-practice" />
          <Tile icon={Moon} label="Tonight" sub={home?.tonight?.title || "An immersion"} onClick={() => navigate(tonightId ? `/inner/${tonightId}` : "/inner")} testid="home-tile-tonight" />
          <Tile icon={TrendingUp} label="Progress" sub="See how you're growing" onClick={() => navigate("/progress")} testid="home-tile-progress" />
          <Tile icon={Flame} label="Daily return" sub="Today's rhythm" onClick={() => navigate("/routine")} testid="home-tile-routine" />
        </motion.div>
      </main>
    </div>
  );
}
